import React, { useState } from 'react';
import { BarChart3, Plus, Trash2, X } from 'lucide-react';
import ChatModalPortal from './ChatModalPortal';

export function ChatPollDialog({ open, onClose, onCreate, isSubmitting = false }) {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [error, setError] = useState('');

  if (!open) return null;

  const submit = async (event) => {
    event.preventDefault();
    const cleanQuestion = question.trim();
    const cleanOptions = options.map((option) => option.trim()).filter(Boolean);
    if (!cleanQuestion || cleanOptions.length < 2) {
      setError('Shkruani pyetjen dhe të paktën dy përgjigje.');
      return;
    }
    if (new Set(cleanOptions.map((option) => option.toLocaleLowerCase())).size !== cleanOptions.length) {
      setError('Përgjigjet duhet të jenë të ndryshme.');
      return;
    }
    setError('');
    const created = await onCreate({ question: cleanQuestion, options: cleanOptions });
    if (created) {
      setQuestion('');
      setOptions(['', '']);
      onClose();
    } else {
      setError('Anketa nuk u krijua. Kontrolloni lidhjen dhe provoni përsëri.');
    }
  };

  return (
    <ChatModalPortal>
      <div
      className="fixed inset-0 z-[1000] flex min-h-screen min-w-full items-center justify-center bg-zinc-950/50 p-4 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isSubmitting) onClose();
      }}
    >
      <form onSubmit={submit} className="w-full max-w-lg overflow-hidden rounded-3xl border-0 bg-white shadow-2xl">
        <div aria-hidden="true" className="flex h-1.5 w-full">
          <span className="flex-1 bg-zinc-950" />
          <span className="flex-1 bg-[#DD0000]" />
          <span className="flex-1 bg-[#FFCE00]" />
        </div>
        <header className="flex items-center justify-between border-b border-zinc-100 px-5 py-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
              <BarChart3 size={18} />
            </span>
            <div>
              <h2 className="text-sm font-extrabold text-zinc-950">Krijo anketë</h2>
              <p className="mt-0.5 text-xs text-zinc-500">Merrni mendimet e bisedës suaj.</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-900" aria-label="Mbyll">
            <X size={17} />
          </button>
        </header>

        <div className="space-y-4 p-5">
          <label className="block space-y-1.5">
            <span className="text-xs font-bold text-zinc-700">Pyetja</span>
            <input
              autoFocus
              maxLength={240}
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              placeholder="Çfarë dëshironi të pyesni?"
              className="h-11 w-full rounded-xl border border-zinc-200 bg-white px-3 text-sm text-zinc-900 outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
            />
          </label>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-zinc-700">Përgjigjet</span>
              <span className="text-[11px] text-zinc-400">{options.length}/10</span>
            </div>
            {options.map((option, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  maxLength={120}
                  value={option}
                  onChange={(event) => setOptions((current) => current.map((item, itemIndex) => itemIndex === index ? event.target.value : item))}
                  placeholder={`Përgjigjja ${index + 1}`}
                  className="h-10 min-w-0 flex-1 rounded-xl border border-zinc-200 bg-white px-3 text-sm outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                />
                {options.length > 2 && (
                  <button type="button" onClick={() => setOptions((current) => current.filter((_, itemIndex) => itemIndex !== index))} className="rounded-lg p-2 text-zinc-400 hover:bg-red-50 hover:text-red-600" aria-label={`Hiq përgjigjen ${index + 1}`}>
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            ))}
            {options.length < 10 && (
              <button type="button" onClick={() => setOptions((current) => [...current, ''])} className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-bold text-[#9A6700] hover:bg-amber-50">
                <Plus size={14} /> Shto përgjigje
              </button>
            )}
          </div>
          {error && <p role="alert" className="text-xs font-medium text-red-600">{error}</p>}
        </div>

        <footer className="flex justify-end gap-2 border-t border-zinc-100 bg-zinc-50/70 px-5 py-4">
          <button type="button" onClick={onClose} disabled={isSubmitting} className="rounded-xl px-4 py-2 text-xs font-bold text-zinc-600 hover:bg-zinc-100 disabled:opacity-50">Anulo</button>
          <button type="submit" disabled={isSubmitting} className="rounded-xl bg-zinc-950 px-4 py-2 text-xs font-bold text-[#FFCE00] transition hover:bg-[#DD0000] hover:text-white disabled:opacity-50">
            {isSubmitting ? 'Duke krijuar...' : 'Dërgo anketën'}
          </button>
        </footer>
      </form>
      </div>
    </ChatModalPortal>
  );
}

export function PollMessage({ message, votes = [], currentUserId, onVote, isVoting = false }) {
  let poll;
  try {
    poll = JSON.parse(message.content);
  } catch {
    return <p className="text-xs text-red-600">Anketa nuk mund të shfaqet.</p>;
  }
  if (!poll?.question || !Array.isArray(poll.options) || poll.options.length < 2) {
    return <p className="text-xs text-red-600">Të dhënat e anketës janë të pavlefshme.</p>;
  }

  const ownVote = votes.find((vote) => vote.user_id === currentUserId);
  const totalVotes = votes.length;

  return (
    <div className="w-full min-w-[220px] max-w-sm space-y-2.5">
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-zinc-500">
        <BarChart3 size={14} className="text-[#9A6700]" /> Anketë
      </div>
      <h3 className="text-sm font-extrabold text-zinc-950">{poll.question}</h3>
      <div className="space-y-1.5">
        {poll.options.map((option, index) => {
          const count = votes.filter((vote) => vote.option_index === index).length;
          const percent = totalVotes ? Math.round((count / totalVotes) * 100) : 0;
          const selected = ownVote?.option_index === index;
          return (
            <button
              key={`${index}-${option}`}
              type="button"
              disabled={isVoting}
              onClick={() => onVote(message, index)}
              aria-pressed={selected}
              className={`relative flex min-h-10 w-full items-center justify-between overflow-hidden rounded-xl border px-3 py-2 text-left text-xs transition disabled:cursor-wait ${selected ? 'border-amber-400 bg-amber-50' : 'border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50'}`}
            >
              {totalVotes > 0 && <span aria-hidden="true" className={`absolute inset-y-0 left-0 ${selected ? 'bg-amber-100' : 'bg-zinc-100'}`} style={{ width: `${percent}%` }} />}
              <span className="relative z-10 font-semibold text-zinc-800">{option}</span>
              <span className="relative z-10 ml-2 shrink-0 text-[10px] font-bold text-zinc-500">{totalVotes ? `${percent}% · ${count}` : ''}</span>
            </button>
          );
        })}
      </div>
      <p className="text-[10px] text-zinc-500">{totalVotes} vota{ownVote ? ' · Voto ruajtur' : ' · Zgjidhni një përgjigje'}</p>
    </div>
  );
}

export default ChatPollDialog;
